import { useCallback, useLayoutEffect, useState } from 'react';

/** Result returned when observing whether an element overflows horizontally. */
interface UseIsElementTruncatedResult<TElement extends HTMLElement> {
  /** Ref callback for the element whose rendered width should be observed. */
  ref: (element: TElement | null) => void;
  /** Whether the element's content currently exceeds its visible width. */
  isTruncated: boolean;
}

/**
 * Observes whether an element's horizontal content is visually truncated.
 *
 * @param content - Optional label content that should trigger remeasurement.
 * @returns A ref callback and the current overflow state.
 */
export const useIsElementTruncated = <TElement extends HTMLElement>(
  content?: string
): UseIsElementTruncatedResult<TElement> => {
  const [element, setElement] = useState<TElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useCallback((nextElement: TElement | null) => {
    setElement(nextElement);
  }, []);

  useLayoutEffect(() => {
    if (!element) return;

    const updateTruncation = () => {
      const contentElement = element.firstElementChild;
      const contentWidth =
        contentElement instanceof HTMLElement
          ? contentElement.scrollWidth
          : element.scrollWidth;
      setIsTruncated(contentWidth > element.clientWidth);
    };

    updateTruncation();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateTruncation);
      return () => window.removeEventListener('resize', updateTruncation);
    }

    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [element, content]);

  return { ref, isTruncated };
};
